import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon, BriefcaseBusinessIcon, GraduationCapIcon, HeadsetIcon, HouseIcon, ShoppingBagIcon, UniversityIcon, UsersIcon, WalletIcon, BoxIcon } from "lucide-react";
interface Module {
  id: string;
  label: string;
  note: string;
  icon: BoxIcon;
  tile: string;
  to: string;
  /** Which side of the identity node the module sits on, on wide screens. */
  side: 'left' | 'right' | 'below';
}

/**
 * The ecosystem, drawn around one identity.
 *
 * The point of the visualisation is the relationship, not the architecture:
 * every module hangs off a single Campuzon identity, which is the actual
 * product model. Each node is a real destination, so this is navigation
 * rather than a diagram.
 */
const MODULES: Module[] = [{
  id: 'academics',
  label: 'Academics',
  note: 'Courses, results, timetable & more',
  icon: GraduationCapIcon,
  tile: 'bg-[#e3f3ee] text-[#0f766e]',
  to: '/academics',
  side: 'left'
}, {
  id: 'campus',
  label: 'Campus',
  note: 'Events, news, communities',
  icon: UniversityIcon,
  tile: 'bg-[#e5eefb] text-[#1d4ed8]',
  to: '/social',
  side: 'left'
}, {
  id: 'marketplace',
  label: 'Marketplace',
  note: 'Food, books, services, gigs & more',
  icon: ShoppingBagIcon,
  tile: 'bg-[#fef0e2] text-[#b45309]',
  to: '/market',
  side: 'left'
}, {
  id: 'housing',
  label: 'Housing',
  note: 'Find places, apply, manage stays',
  icon: HouseIcon,
  tile: 'bg-[#e7eafc] text-[#3730a3]',
  to: '/housing',
  side: 'right'
}, {
  id: 'money',
  label: 'Money',
  note: 'Fees, payments, records',
  icon: WalletIcon,
  tile: 'bg-[#e3f3ee] text-[#0f766e]',
  to: '/money',
  side: 'right'
}, {
  id: 'opportunities',
  label: 'Opportunities',
  note: 'Jobs, internships, scholarships',
  icon: BriefcaseBusinessIcon,
  tile: 'bg-[#f2e9fa] text-[#7e22ce]',
  to: '/opportunities',
  side: 'right'
}, {
  id: 'services',
  label: 'Services & support',
  note: 'Transcripts, requests, counselling & more',
  icon: HeadsetIcon,
  tile: 'bg-[#fce8ef] text-[#be185d]',
  to: '/services',
  side: 'below'
}];
function ModuleCard({
  module
}: {
  module: Module;
}) {
  const Icon = module.icon;
  return <Link to={module.to} className="group flex min-h-touch-lg items-center gap-3 rounded-card border border-line bg-surface px-4 py-3.5 transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-[0_10px_28px_rgba(15,23,42,0.08)]" data-id="element-2214" data-mp="3jah3wkh0pknb">
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${module.tile}`} data-id="element-2215" data-mp="2mkgfimkk966j">
        <Icon aria-hidden className="h-5 w-5" data-id="element-2216" data-mp="30jptrgp7hq7v" />
      </span>
      <span className="min-w-0 flex-1" data-id="element-2217" data-mp="30h3fky7t8cgo">
        <span className="block text-body font-semibold text-ink" data-id="element-2218" data-mp="2vjblpynlug9u">{module.label}</span>
        <span className="block text-caption leading-5 text-ink-muted" data-id="element-2219" data-mp="fspbx62vt0p4">{module.note}</span>
      </span>
      <ArrowRightIcon aria-hidden className="h-4 w-4 shrink-0 text-ink-subtle opacity-0 transition-[opacity,transform] duration-150 ease-out group-hover:translate-x-0.5 group-hover:opacity-100" data-id="element-2220" data-mp="auxbfno6869b" />
    </Link>;
}
export function EcosystemMap() {
  const [hovered, setHovered] = useState<string | null>(null);
  const left = MODULES.filter(m => m.side === 'left');
  const right = MODULES.filter(m => m.side === 'right');
  const below = MODULES.filter(m => m.side === 'below');
  return <section aria-labelledby="ecosystem-heading" className="bg-surface py-16 lg:py-20" data-id="element-2221" data-mp="121w5l46754y3">
      <div className="mx-auto max-w-7xl px-4 sm:px-6" data-id="element-2222" data-mp="170qyiyxvo9vx">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between" data-id="element-2223" data-mp="16zfle8ijlps5">
          <h2 id="ecosystem-heading" className="max-w-[22ch] text-h2 font-bold tracking-tight text-ink" data-id="element-2224" data-mp="sat25vpsodvn">
            Everything your campus needs.
            <br data-id="element-2225" data-mp="x8kyr31dwo66" />
            Connected in <span className="text-brand-700" data-id="element-2226" data-mp="1l08fwt8ugkv9">one place.</span>
          </h2>
          <Link to="/welcome/campuses" className="group inline-flex min-h-touch shrink-0 items-center gap-2 self-start rounded-lg border border-line bg-surface px-4 text-body-sm font-semibold text-ink transition-colors duration-150 ease-out hover:border-line-strong sm:self-auto" data-id="element-2227" data-mp="1pr8cfxe1x0x8">
            Explore all features
            <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-2228" data-mp="3o9lhav7ovkps" />
          </Link>
        </div>

        <div className="relative mt-10 overflow-hidden rounded-panel bg-[#f4faf8] px-4 py-10 sm:px-8 lg:px-12 lg:py-14" onMouseLeave={() => setHovered(null)} data-id="element-2229" data-mp="3jd4x2c3m1qhz">
          {/* Connector field. Decorative, and hidden where the layout stacks. */}
          <svg aria-hidden className="pointer-events-none absolute inset-0 hidden h-full w-full lg:block" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-2230" data-mp="b31zpfbyssju">
            <g stroke="#a7d5c8" strokeWidth="0.15" strokeDasharray="1.2 1.2" fill="none" data-id="element-2231" data-mp="62e7iqc025o1">
              <path d="M31 22 H42 V48" data-id="element-2232" data-mp="13l7boxj79yvl" />
              <path d="M31 50 H44" data-id="element-2233" data-mp="ysnvdst4opn3" />
              <path d="M31 78 H42 V52" data-id="element-2234" data-mp="yse667fwqmig" />
              <path d="M69 22 H58 V48" data-id="element-2235" data-mp="164bzwnfuseo3" />
              <path d="M69 50 H56" data-id="element-2236" data-mp="165dozvtpf1ot" />
              <path d="M69 78 H58 V52" data-id="element-2237" data-mp="166f020apzo6j" />
              <path d="M50 60 V80" data-id="element-2238" data-mp="1b2vlunug5qgg" />
            </g>
          </svg>

          <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:items-center lg:gap-10" data-id="element-2239" data-mp="1b58dxttx5h78">
            <ul className="space-y-4" data-id="element-2240" data-mp="1l1bhn44f5nwa">
              {left.map(module => <li key={module.id} onMouseEnter={() => setHovered(module.id)} className={hovered && hovered !== module.id ? 'opacity-60 transition-opacity duration-200 ease-out' : 'transition-opacity duration-200 ease-out'} data-id="element-2241" data-mp="1l2wberiwt2t3">
                  <ModuleCard module={module} data-id="element-2242" data-mp="1yszp2u3q3o6x" />
                </li>)}
            </ul>

            {/* Identity node */}
            <div className="flex flex-col items-center justify-center py-2" data-id="element-2243" data-mp="23sm1j1bq90eo">
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-700 ring-8 ring-white/70" data-id="element-2244" data-mp="28r5sjg56h2dw">
                <UsersIcon aria-hidden className="h-8 w-8 text-white" data-id="element-2245" data-mp="2cs92xn4gi67q" />
              </span>
              <p className="mt-3 text-center text-caption font-bold uppercase tracking-wide text-brand-800" data-id="element-2246" data-mp="dnhgtmdxp9gr">
                Campuzon
                <br data-id="element-2247" data-mp="8q7mptflo9ml" />
                identity
              </p>
            </div>

            <ul className="space-y-4" data-id="element-2248" data-mp="mhcniyupf7yi">
              {right.map(module => <li key={module.id} onMouseEnter={() => setHovered(module.id)} className={hovered && hovered !== module.id ? 'opacity-60 transition-opacity duration-200 ease-out' : 'transition-opacity duration-200 ease-out'} data-id="element-2249" data-mp="hpm8ifktnw0r">
                  <ModuleCard module={module} data-id="element-2250" data-mp="rg73zu578kfg" />
                </li>)}
            </ul>
          </div>

          <div className="relative mt-6 flex justify-center" data-id="element-2251" data-mp="15fgjssphtciw">
            <ul className="w-full max-w-md" data-id="element-2252" data-mp="15cud0vaff11q">
              {below.map(module => <li key={module.id} onMouseEnter={() => setHovered(module.id)} data-id="element-2253" data-mp="11e3c0d2uaz9c">
                  <ModuleCard module={module} data-id="element-2254" data-mp="377l7frzylnnt" />
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}